import api, { publicApi } from './api';

export interface DemoRequestData {
  fullName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  productType: string;
  message: string;
  captchaToken: string;
}

export interface DemoRequest {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  productType: string;
  message: string;
  status: 'pending' | 'contacted' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  submittedAt: string;
  contactedAt?: string;
  notes?: string;
  assignedTo?: string;
  source: string;
  ipAddress?: string;
  userAgent?: string;
  formattedPhone?: string;
  timeSinceSubmission?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DemoRequestListResponse {
  demoRequests: DemoRequest[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: {
    status?: string;
    productType?: string;
    priority?: string;
    search?: string;
  };
}

export interface DemoRequestStats {
  total: number;
  pending: number;
  contacted: number;
  completed: number;
  cancelled: number;
  highPriority: number;
  thisWeek: number;
  thisMonth: number;
}

export interface DemoRequestStatsResponse {
  stats: DemoRequestStats;
  productTypeStats: Array<{
    _id: string;
    count: number;
  }>;
}

export interface DemoRequestFilters {
  page?: number;
  limit?: number;
  status?: string;
  productType?: string;
  priority?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

class DemoRequestService {
  /**
   * Submit a new demo request
   */
  async submitDemoRequest(data: DemoRequestData): Promise<{ demoRequest: Partial<DemoRequest> }> {
    try {
      const response = await publicApi.post('/demo-requests', data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to submit demo request. Please try again.'
      );
    }
  }

  /**
   * Get all demo requests (Admin only)
   */
  async getDemoRequests(filters: DemoRequestFilters = {}): Promise<DemoRequestListResponse> {
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/demo-requests?${params.toString()}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to retrieve demo requests');
    }
  }

  /**
   * Get demo request by ID (Admin only)
   */
  async getDemoRequestById(id: string): Promise<DemoRequest> {
    try {
      const response = await api.get(`/demo-requests/${id}`);
      return response.data.data.demoRequest;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to retrieve demo request');
    }
  }

  /**
   * Update demo request (Admin only)
   */
  async updateDemoRequest(
    id: string,
    updates: {
      status?: string;
      notes?: string;
      assignedTo?: string;
      priority?: string;
    }
  ): Promise<DemoRequest> {
    try {
      const response = await api.put(`/demo-requests/${id}`, updates);
      return response.data.data.demoRequest;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update demo request');
    }
  }

  /**
   * Delete demo request (Admin only)
   */
  async deleteDemoRequest(id: string): Promise<void> {
    try {
      await api.delete(`/demo-requests/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete demo request');
    }
  }

  /**
   * Get demo request statistics (Admin only)
   */
  async getDemoRequestStats(): Promise<DemoRequestStatsResponse> {
    try {
      const response = await api.get('/demo-requests/stats');
      return response.data.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to retrieve demo request statistics'
      );
    }
  }

  /**
   * Export demo requests (Admin only)
   */
  async exportDemoRequests(filters: DemoRequestFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/demo-requests/export?${params.toString()}`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to export demo requests');
    }
  }

  /**
   * Get product type options
   */
  getProductTypeOptions() {
    return [
      { value: 'project-management', label: 'Project Management' },
      { value: 'project-financials', label: 'Project Financials' },
      { value: 'quality-safety', label: 'Quality & Safety' },
      { value: 'design-coordination', label: 'Design Coordination' },
      { value: 'invoice-management', label: 'Invoice Management' },
      { value: 'tender-management', label: 'Tender Management' },
      { value: 'full-platform', label: 'Full Platform' },
    ];
  }

  /**
   * Get status options
   */
  getStatusOptions() {
    return [
      { value: 'all', label: 'All Statuses' },
      { value: 'pending', label: 'Pending' },
      { value: 'contacted', label: 'Contacted' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' },
    ];
  }

  /**
   * Get priority options
   */
  getPriorityOptions() {
    return [
      { value: 'all', label: 'All Priorities' },
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ];
  }

  /**
   * Get status badge color
   */
  getStatusBadgeColor(status: string): string {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'contacted':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Get priority badge color
   */
  getPriorityBadgeColor(priority: string): string {
    switch (priority) {
      case 'low':
        return 'bg-gray-100 text-gray-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}

export default new DemoRequestService();
